import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSubject($filter: subjectFilterInput) {
    getSubject(filter: $filter) {
      id
      subjectType
      groupId
      name
      gender
      dob
      bloodGroup
      photo
      email
      mobile
      address
      location
      organizationId
      managingUserId
      updateReferenceId
      ncdConfirmedType
      dmConfirmationDate
      htConfirmationDate
      lastReadingDatetimeUnix
      nextReadingDate
      isArchived
      managingUser {
        systemUser {
          username
        }
        name
      }
      organization {
        logoUrl
        name
        id
      }
      subjectAnc {
        lmpDate
        id
      }
      subjectMetric {
        isHypertensive
        isDiabetic
        isSmoker
        bsa
        bmi
        weight
        height
        id
      }
      keyValueList {
        organizationSubjectKey {
          id
          name
          priorityOrder
          isMandatory
        }
        value
        organizationSubjectKeyId
        subjectId
        id
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.SubjectState = {
  result: null,
  error: null,
  loading: false
};

export const getSubjectRequest = createAsyncThunk<
  TYPES.Subject,
  {filter?: TYPES.SubjectFilterInput;},
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'subject/get_subject',
  async (params: {filter?: TYPES.SubjectFilterInput;}, { extra: client }) => {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSubject;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSubjectSlice = createSlice({
  name: 'get_subject',
  initialState: initialState,
  reducers: {
    getSubjectClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSubjectRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getSubjectRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getSubjectRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSubjectClear } = getSubjectSlice.actions;

export const getSubject = createSelector(
  (state: RootState) => state.subject.getSubject.result,
  (state: RootState) => state.subject.getSubject.error,
  (state: RootState) => state.subject.getSubject.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSubjectSlice.reducer;